/**
 * Branding Assets Service
 * Handles logo and overlay asset management in Supabase
 */

import { supabase, STORAGE_BUCKET } from '@/lib/supabase';
import { BrandingAsset, BrandingAssetType, DbBrandingAsset } from '@/types';
import { classifyAspect, aspectToken } from '@/lib/overlayVariants';

// ============================================================================
// Asset Operations
// ============================================================================

/**
 * Fetch all branding assets for a user
 */
export const fetchBrandingAssets = async (
  userId: string,
  type?: BrandingAssetType
): Promise<BrandingAsset[]> => {
  let query = supabase
    .from('branding_assets')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (type) {
    query = query.eq('type', type);
  }

  const { data, error } = await query;

  if (error) {
    throw new Error(`Assets konnten nicht geladen werden: ${error.message}`);
  }

  return (data as DbBrandingAsset[]).map(mapDbAssetToAsset);
};

/**
 * Upload a new branding asset
 */
export const uploadBrandingAsset = async (
  userId: string,
  file: File,
  type: BrandingAssetType
): Promise<BrandingAsset> => {
  // Generate unique filename
  const timestamp = Date.now();
  const randomStr = Math.random().toString(36).substring(2, 8);
  const ext = file.name.split('.').pop() || 'png';
  const fileName = `${timestamp}-${randomStr}.${ext}`;
  const storagePath = `assets/${userId}/${type}s/${fileName}`;

  // Upload to storage
  const { error: uploadError } = await supabase.storage
    .from(STORAGE_BUCKET)
    .upload(storagePath, file, {
      cacheControl: '3600',
      upsert: false,
    });

  if (uploadError) {
    throw new Error(`Upload fehlgeschlagen: ${uploadError.message}`);
  }

  // Extract original filename without extension as name
  const originalName = file.name.replace(/\.[^/.]+$/, '');

  // Save reference in database
  const { data, error } = await supabase
    .from('branding_assets')
    .insert({
      user_id: userId,
      type,
      name: originalName,
      storage_path: storagePath,
    })
    .select()
    .single();

  if (error) {
    // Rollback: Delete uploaded file
    await supabase.storage.from(STORAGE_BUCKET).remove([storagePath]);
    throw new Error(`Asset konnte nicht gespeichert werden: ${error.message}`);
  }

  return mapDbAssetToAsset(data as DbBrandingAsset);
};

/** Seitenverhältnis (Breite/Höhe) einer Bilddatei ermitteln. */
function imageAspectRatio(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img.naturalHeight > 0 ? img.naturalWidth / img.naturalHeight : 4 / 3);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Bild konnte nicht geladen werden'));
    };
    img.src = url;
  });
}

/**
 * Lädt ein vom Nutzer hochgeladenes Overlay hoch und erkennt automatisch das
 * nächstliegende Seitenverhältnis (4:3 oder 1:1). Der Aspekt-Token landet im
 * Dateinamen, damit das Overlay an der Varianten-Gruppierung teilnimmt (Auswahl/
 * Anwendung/Löschen wie Builder-Overlays).
 */
export const uploadOverlayAsset = async (userId: string, file: File): Promise<BrandingAsset> => {
  let ratio = 4 / 3;
  try {
    ratio = await imageAspectRatio(file);
  } catch {
    // Ratio nicht ermittelbar → Default 4:3.
  }
  const token = aspectToken(classifyAspect(ratio));
  const base =
    (file.name.replace(/\.[^/.]+$/, '') || 'overlay').replace(/[^\w]+/g, '-').replace(/^-+|-+$/g, '') || 'overlay';
  const ext = file.name.split('.').pop() || 'png';
  const named = new File([file], `${base}-${token}-${Date.now()}.${ext}`, { type: file.type });
  return uploadBrandingAsset(userId, named, 'overlay');
};

/**
 * Delete a branding asset
 */
export const deleteBrandingAsset = async (
  assetId: string,
  storagePath: string
): Promise<void> => {
  // Delete from storage
  const { error: storageError } = await supabase.storage
    .from(STORAGE_BUCKET)
    .remove([storagePath]);

  if (storageError) {
    console.warn('Storage deletion failed:', storageError.message);
  }

  // Delete from database
  const { error } = await supabase
    .from('branding_assets')
    .delete()
    .eq('id', assetId);

  if (error) {
    throw new Error(`Asset konnte nicht gelöscht werden: ${error.message}`);
  }
};

/**
 * Update asset name
 */
export const updateBrandingAssetName = async (
  assetId: string,
  name: string
): Promise<void> => {
  const { error } = await supabase
    .from('branding_assets')
    .update({ name })
    .eq('id', assetId);

  if (error) {
    throw new Error(`Name konnte nicht aktualisiert werden: ${error.message}`);
  }
};

// ============================================================================
// Helpers
// ============================================================================

const mapDbAssetToAsset = (db: DbBrandingAsset): BrandingAsset => {
  const { data } = supabase.storage
    .from(STORAGE_BUCKET)
    .getPublicUrl(db.storage_path);

  return {
    id: db.id,
    userId: db.user_id,
    type: db.type,
    name: db.name,
    storagePath: db.storage_path,
    publicUrl: data.publicUrl,
    createdAt: db.created_at,
  };
};

